/**
 * 富文本工具：文本节点内容以 HTML 形态存储（行内编辑与放大编辑均为富文本），
 * 下游消费（拼提示词）转纯文本，渲染前经白名单净化。
 *
 * 兼容：历史纯文本内容不含标签，looksLikeHtml 为 false 时全部按纯文本处理。
 */

const RICH_TAG_RE = /<\/?(p|div|br|b|strong|i|em|u|s|strike|h[1-6]|ul|ol|li|blockquote|pre|code|span)\b/i;

/** 内容是否为富文本 HTML（历史纯文本返回 false，按原样处理） */
export function looksLikeHtml(content: string): boolean {
  return RICH_TAG_RE.test(content);
}

/**
 * 富文本 → 纯文本：块级元素与 br 转换行，其余取 textContent（实体自动解码）。
 * 供 aggregateUpstreamText 等下游消费使用，保证 HTML 标签不漏进提示词。
 */
export function htmlToPlainText(html: string): string {
  if (!looksLikeHtml(html)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')));
  doc.body
    .querySelectorAll('p,div,h1,h2,h3,h4,h5,h6,li,blockquote,pre')
    .forEach((el) => el.after(doc.createTextNode('\n')));
  return (doc.body.textContent ?? '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

/** 富文本允许的标签白名单（其余标签解包保留文本，script/style 等整体移除） */
const ALLOWED_TAGS = new Set([
  'P', 'DIV', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'CODE', 'PRE', 'SPAN',
]);
/** 危险标签：连同内容整体移除 */
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'SVG', 'IMG', 'VIDEO', 'AUDIO', 'FORM', 'INPUT']);

/**
 * 白名单净化富文本 HTML：画布快照可导出/导入，渲染前必须净化防存储型 XSS。
 * 允许标签剥离全部属性（样式由标签语义承担），其余标签解包保留文本。
 */
export function sanitizeRichTextHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const processNode = (node: Node): void => {
    // 收集子节点后递归（处理过程中会改动兄弟结构）
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const el = child as HTMLElement;
      const tag = el.tagName;
      if (DROP_TAGS.has(tag)) {
        el.remove();
        continue;
      }
      processNode(el);
      if (!ALLOWED_TAGS.has(tag)) {
        // 解包：用子节点替换自身（保留文本内容）
        const parent = el.parentNode;
        if (!parent) continue;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      } else {
        while (el.attributes.length > 0) el.removeAttribute(el.attributes[0].name);
      }
    }
  };

  processNode(doc.body);
  return doc.body.innerHTML;
}
